// Inventory Validation System - FIXED VERSION
// Prevents adding more items than available inventory
// Shows accurate, real-time stock warnings and enforces proper limits

class InventoryValidator {
    constructor() {
        this.debounceTimer = null;
        this.lastCartCheck = 0;
        this.cachedCart = null;
        this.init();
    }

    init() {
        console.log('🔍 Initializing Enhanced Inventory Validator...');

        // Don't initialize on cart page - it has different validation needs
        if (window.location.pathname === '/cart' || window.location.pathname.includes('/cart')) {
            console.log('Skipping inventory validation on cart page');
            return;
        }

        this.setupValidation();
    }

    setupValidation() {
        // Check if this is a single-variant product (no size dropdown)
        this.isSingleVariantProduct = !document.querySelector('.size-dropdown-button');

        this.setupProductFormValidation();
        this.setupVariantChangeListener();
        this.setupCartUpdateListener();

        // CRITICAL: Immediately check and update all variant states on page load
        // This ensures sold out items are properly marked even when navigating between pages
        if (this.isSingleVariantProduct) {
            this.updateSingleVariantStatus();
        } else {
            this.updateInventoryWarnings();
        }

        // ALSO: Set up periodic checking to catch any state changes
        this.setupPeriodicInventoryCheck();
    }

    // Setup periodic inventory checking to catch state changes
    setupPeriodicInventoryCheck() {
        // Check inventory every 30 seconds to catch any changes
        setInterval(() => {
            if (this.isSingleVariantProduct) {
                this.updateSingleVariantStatus();
            } else {
                this.updateInventoryWarnings();
            }
        }, 30000);
    }

    // Get inventory data for a variant - IMPROVED to handle multiple data sources
    getVariantInventory(variantId) {
        // Try multiple sources for inventory data
        let variant = null;

        // 1. Try global product variants inventory (most reliable for product pages)
        if (window.productVariantsInventory && window.productVariantsInventory[variantId]) {
            variant = window.productVariantsInventory[variantId];
        }

        // 2. Try cart inventory data as fallback (for cart page)
        if (!variant && window.cartInventoryData && window.cartInventoryData[variantId]) {
            variant = window.cartInventoryData[variantId];
        }

        if (!variant) {
            console.warn('❌ No inventory data found for variant:', variantId);
            console.warn('Available variants in productVariantsInventory:', window.productVariantsInventory ? Object.keys(window.productVariantsInventory) : 'undefined');
            console.warn('Available variants in cartInventoryData:', window.cartInventoryData ? Object.keys(window.cartInventoryData) : 'undefined');
            return null;
        }

        // Debug log to verify data integrity
        if (variant.inventory_quantity === undefined || variant.inventory_quantity === null) {
            console.error('⚠️ Variant has undefined inventory_quantity:', {
                variantId,
                variant,
                source: window.productVariantsInventory && window.productVariantsInventory[variantId] ? 'productVariantsInventory' : 'cartInventoryData'
            });
        }

        return variant;
    }

    // Get available quantity for a variant - COMPLETELY REWRITTEN for accuracy
    async getAvailableQuantity(variantId) {
        const variant = this.getVariantInventory(variantId);
        if (!variant || !this.isInventoryManaged(variant)) {
            console.log(`📊 Variant ${variantId}: No inventory management - unlimited`);
            return Infinity; // No limit if not managed
        }

        // CRITICAL FIX: Don't rely solely on variant.available - it can be wrong
        // Instead, focus on actual inventory numbers and policy

        // Get current cart with caching to prevent excessive API calls
        const cart = await this.getCachedCart();
        if (!cart) {
            // If we can't get cart data, be conservative and use full inventory
            console.log(`📊 Variant ${variantId}: Can't get cart data - using full inventory (${variant.inventory_quantity})`);
            return Math.max(0, variant.inventory_quantity);
        }

        let quantityInCart = 0;
        cart.items.forEach(item => {
            if (item.variant_id === parseInt(variantId)) {
                quantityInCart += item.quantity;
            }
        });

        const availableQuantity = Math.max(0, variant.inventory_quantity - quantityInCart);

        // IMPROVED: Check inventory policy for actual availability
        let isActuallyAvailable = true;

        // If inventory is managed and policy is 'deny', check actual stock
        if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
            isActuallyAvailable = availableQuantity > 0;
        }
        // If inventory policy is 'continue', item is available even at 0 stock
        else if (variant.inventory_policy === 'continue') {
            isActuallyAvailable = true;
        }
        // If no inventory management, rely on the available flag but don't be strict
        else {
            isActuallyAvailable = variant.available !== false;
        }

        console.log(`📊 Inventory DEBUG for variant ${variantId}:`, {
            title: variant.title,
            total_inventory: variant.inventory_quantity,
            quantity_in_cart: quantityInCart,
            calculated_available: availableQuantity,
            shopify_available_flag: variant.available,
            inventory_management: variant.inventory_management,
            inventory_policy: variant.inventory_policy,
            final_is_available: isActuallyAvailable,
            final_quantity: isActuallyAvailable ? availableQuantity : 0
        });

        // Return 0 only if truly unavailable based on policy and stock
        return isActuallyAvailable ? availableQuantity : 0;
    }

    // Cached cart fetching to prevent API spam
    async getCachedCart() {
        const now = Date.now();

        // Use cached cart if it's less than 1 second old (reduced for better accuracy)
        if (this.cachedCart && (now - this.lastCartCheck) < 1000) {
            return this.cachedCart;
        }

        try {
            const response = await fetch('/cart.js');
            this.cachedCart = await response.json();
            this.lastCartCheck = now;
            return this.cachedCart;
        } catch (error) {
            console.error('Error fetching cart:', error);
            return null;
        }
    }

    // Clear cached cart data when cart is updated
    clearCartCache() {
        this.cachedCart = null;
        this.lastCartCheck = 0;
    }

    // Check if variant has inventory management
    isInventoryManaged(variant) {
        return variant.inventory_management === 'shopify';
    }

    // Update size labels with availability information - IMPROVED for custom dropdowns
    async updateSizeLabels() {
        // Handle both size buttons AND radio inputs in dropdowns
        const sizeButtons = document.querySelectorAll('.size-option-button');
        const sizeRadios = document.querySelectorAll('.size-option input[type="radio"]');

        // Update traditional size buttons
        for (const button of sizeButtons) {
            const variantId = button.getAttribute('data-variant-id');
            if (!variantId) continue;

            const variant = this.getVariantInventory(variantId);
            if (!variant) continue;

            const availableQuantity = await this.getAvailableQuantity(variantId);
            const isSoldOut = availableQuantity === 0;

            // Update button state
            if (isSoldOut) {
                button.classList.add('sold-out');
                button.disabled = true;

                // Update button text to show sold out
                const sizeText = button.querySelector('.size-text');
                if (sizeText && !sizeText.textContent.includes('SOLD OUT')) {
                    sizeText.dataset.originalText = sizeText.textContent;
                    sizeText.textContent = sizeText.textContent + ' - SOLD OUT';
                }
            } else {
                button.classList.remove('sold-out');
                button.disabled = false;

                // Restore original text if it was modified
                const sizeText = button.querySelector('.size-text');
                if (sizeText && sizeText.dataset.originalText) {
                    sizeText.textContent = sizeText.dataset.originalText;
                    delete sizeText.dataset.originalText;
                }
            }
        }

        // Update radio inputs in custom dropdowns
        for (const radio of sizeRadios) {
            const variantId = radio.value;
            if (!variantId) continue;

            const variant = this.getVariantInventory(variantId);
            if (!variant) continue;

            const availableQuantity = await this.getAvailableQuantity(variantId);
            const isSoldOut = availableQuantity === 0;

            // Update radio button and parent container state
            const sizeOption = radio.closest('.size-option');
            const label = radio.nextElementSibling;

            if (isSoldOut) {
                radio.disabled = true;
                sizeOption?.classList.add('sold-out');

                // NO NEED TO UPDATE TEXT - Liquid template already handles "(SOLD OUT)" text
                // The Liquid template adds the sold out text based on variant.available
                // JavaScript inventory validation should only handle styling, not text modification
            } else {
                radio.disabled = false;
                sizeOption?.classList.remove('sold-out');

                // NO NEED TO RESTORE TEXT - we're not modifying it anymore
                // Let the Liquid template handle all text content
            }
        }

        // Check if any options are available
        const availableButtons = Array.from(sizeButtons).filter(btn => !btn.disabled);
        const availableRadios = Array.from(sizeRadios).filter(radio => !radio.disabled);
        const hasAvailableOptions = availableButtons.length > 0 || availableRadios.length > 0;

        // Get currently selected option - CHECK BOTH BUTTONS AND RADIOS
        const selectedButton = document.querySelector('.size-option-button.selected:not(.sold-out)');
        const selectedRadio = document.querySelector('.size-option input[type="radio"]:checked:not(:disabled)');
        const selectedOption = selectedButton || selectedRadio;

        console.log('🔍 Button state check:', {
            hasAvailableOptions,
            selectedButton: !!selectedButton,
            selectedRadio: !!selectedRadio,
            selectedOption: !!selectedOption,
            selectedVariantId: selectedRadio?.value || selectedButton?.getAttribute('data-variant-id')
        });

        // Update add to cart button
        this.updateAddToCartButton(hasAvailableOptions, selectedOption);
    }

    // Refresh dropdown display state to match selected radio button
    refreshDropdownState() {
        const sizeSelectedText = document.querySelector('.size-selected-text');
        const selectedRadio = document.querySelector('.size-option input[type="radio"]:checked:not(:disabled)');

        if (!sizeSelectedText) return;

        if (selectedRadio) {
            // Extract just the size part from the label (without inventory info or sold out text)
            const label = selectedRadio.nextElementSibling;
            if (label) {
                let labelText = label.textContent.trim();
                // Remove everything after " -" (like "- ONLY X REMAINING")
                labelText = labelText.split(' -')[0].trim();
                // Remove everything after "(" (like "(SOLD OUT)")
                labelText = labelText.split('(')[0].trim();

                sizeSelectedText.textContent = labelText;
                console.log('🔄 Updated dropdown text to match selected radio:', labelText);
            }
        } else {
            // No valid selection - reset to default
            const defaultPlaceholder = sizeSelectedText.getAttribute('data-default') || 'PRODUCT SIZE';
            sizeSelectedText.textContent = defaultPlaceholder;
            console.log('🔄 Reset dropdown to default placeholder');
        }
    }

    // Handle single-variant products (like bags/accessories without size options)
    async updateSingleVariantStatus() {
        const autoSelectedVariant = document.querySelector('.auto-selected-variant');
        const addToCartButton = document.querySelector('.add-to-cart');
        const stockStatusContainer = document.querySelector('.single-variant-info');

        if (!autoSelectedVariant) return;

        const variantId = autoSelectedVariant.value;
        const variant = this.getVariantInventory(variantId);

        if (!variant) return;

        const availableQuantity = await this.getAvailableQuantity(variantId);
        const isSoldOut = availableQuantity === 0;

        console.log('🔍 Single variant status check:', {
            variantId,
            availableQuantity,
            shopify_available_flag: variant.available,
            isSoldOut,
            inventory_management: variant.inventory_management,
            inventory_policy: variant.inventory_policy
        });

        // Update add to cart button - CRITICAL FIX
        if (addToCartButton) {
            const buttonSpan = addToCartButton.querySelector('span');

            if (isSoldOut) {
                addToCartButton.classList.add('disabled');
                addToCartButton.setAttribute('disabled', 'disabled');
                if (buttonSpan) {
                    buttonSpan.textContent = 'SOLD OUT';
                }
                console.log('Set add to cart button to SOLD OUT');
            } else {
                addToCartButton.classList.remove('disabled');
                addToCartButton.removeAttribute('disabled');
                if (buttonSpan) {
                    buttonSpan.textContent = 'ADD TO CART';
                }
                console.log('Set add to cart button to ADD TO CART');
            }
        }

        // Update stock status display
        if (stockStatusContainer && this.isInventoryManaged(variant)) {
            let existingMessage = stockStatusContainer.querySelector('.stock-status-message');

            if (availableQuantity > 0 && availableQuantity < 10) {
                if (!existingMessage) {
                    existingMessage = document.createElement('div');
                    existingMessage.className = 'stock-status-message';
                    stockStatusContainer.appendChild(existingMessage);
                }
                existingMessage.textContent = `ONLY ${availableQuantity} REMAINING`;
                existingMessage.style.display = 'block';
            } else if (existingMessage) {
                existingMessage.style.display = 'none';
            }
        }
    }

    // Update add to cart button based on availability and selection
    updateAddToCartButton(hasAvailableOptions, currentlySelectedOption = null) {
        const addToCartButton = document.querySelector('.add-to-cart');

        if (!addToCartButton) return;

        const buttonSpan = addToCartButton.querySelector('span');

        if (!hasAvailableOptions) {
            // No available options at all
            addToCartButton.classList.add('disabled');
            addToCartButton.setAttribute('disabled', 'disabled');
            if (buttonSpan) {
                buttonSpan.textContent = 'SOLD OUT';
            }
            console.log('Add to cart button: SOLD OUT (no available options)');
        } else if (currentlySelectedOption) {
            // Has available options AND something is selected
            addToCartButton.classList.remove('disabled');
            addToCartButton.removeAttribute('disabled');
            if (buttonSpan) {
                buttonSpan.textContent = 'ADD TO CART';
            }
            console.log('Add to cart button: ADD TO CART (valid selection)');
        } else {
            // Has available options but nothing selected
            addToCartButton.classList.add('disabled');
            addToCartButton.setAttribute('disabled', 'disabled');
            if (buttonSpan) {
                buttonSpan.textContent = 'ADD TO CART';
            }
            console.log('Add to cart button: DISABLED (no selection)');
        }
    }

    // Update inventory warnings when variant changes - FIXED FOR MULTI-VARIANT
    async updateInventoryWarnings() {
        if (this.isSingleVariantProduct) {
            await this.updateSingleVariantStatus();
        } else {
            // Update all size labels with current availability
            await this.updateSizeLabels();

            // Check if any options are available and what's currently selected
            const availableRadios = Array.from(document.querySelectorAll('.size-option input[type="radio"]')).filter(radio => !radio.disabled);
            const hasAvailableOptions = availableRadios.length > 0;

            // Get currently selected option
            const selectedRadio = document.querySelector('.size-option input[type="radio"]:checked');

            // Update add to cart button based on availability and selection
            this.updateAddToCartButton(hasAvailableOptions, selectedRadio);

            // CRITICAL: Refresh dropdown state to match current selection
            this.refreshDropdownState();

            console.log('🔍 Multi-variant update:', {
                availableOptions: availableRadios.length,
                hasSelection: !!selectedRadio,
                selectedVariantId: selectedRadio?.value
            });
        }

        // Get currently selected variant ID for further processing
        const variantInput = document.querySelector('input[name="id"]:checked') ||
            document.querySelector('input[name="id"][type="hidden"]');

        if (!variantInput) return;

        const variantId = variantInput.value;
        const variant = this.getVariantInventory(variantId);

        if (!variant) return;

        const availableQuantity = await this.getAvailableQuantity(variantId);

        // Update quantity input max attribute
        const quantityInput = document.querySelector('input[name="quantity"]');
        if (quantityInput && this.isInventoryManaged(variant)) {
            quantityInput.setAttribute('max', availableQuantity);
        }
    }

    // Setup product form validation - IMPROVED with debouncing
    setupProductFormValidation() {
        const productForms = document.querySelectorAll('form[action*="/cart/add"]');

        // NOTE: Form submission validation is now handled by custom-cart-interactions.js
        // This ensures proper order of operations and prevents duplicate event listeners

        productForms.forEach(form => {
            // REMOVED: form.addEventListener('submit', ...) 
            // Validation is now called directly from cart interactions before form processing

            // Listen for quantity input changes with debouncing
            const quantityInput = form.querySelector('input[name="quantity"]');
            if (quantityInput) {
                quantityInput.addEventListener('input', () => {
                    clearTimeout(this.debounceTimer);
                    this.debounceTimer = setTimeout(() => {
                        this.validateQuantityInput(quantityInput);
                    }, 300); // 300ms debounce
                });

                quantityInput.addEventListener('change', () => this.validateQuantityInput(quantityInput));
            }
        });
    }

    // Validate product form before submission - IMPROVED error handling
    async validateProductForm(form) {
        const variantInput = form.querySelector('input[name="id"]:checked') ||
            form.querySelector('input[name="id"][type="hidden"]');
        const quantityInput = form.querySelector('input[name="quantity"]');

        // Check if a variant is selected
        if (!variantInput) {
            this.showInventoryErrorModal(0, 1, 'Please select a size before adding to cart');
            return false;
        }

        const variantId = variantInput.value;
        const requestedQuantity = parseInt(quantityInput?.value) || 1;
        const variant = this.getVariantInventory(variantId);

        if (!variant) {
            this.showInventoryErrorModal(0, requestedQuantity, 'Invalid product variant');
            return false;
        }

        // CRITICAL: Force fresh cart data by clearing cache and getting latest
        this.clearCartCache();
        const availableQuantity = await this.getAvailableQuantity(variantId);

        console.log(`🔍 FORM VALIDATION - Variant ${variantId}:`, {
            title: variant.title,
            requested: requestedQuantity,
            available: availableQuantity,
            inventory_total: variant.inventory_quantity,
            timestamp: new Date().toISOString()
        });

        // Check if variant is available at all
        if (availableQuantity === 0) {
            this.showInventoryErrorModal(0, requestedQuantity, variant.title);
            return false;
        }

        // Check if requested quantity exceeds available stock
        if (this.isInventoryManaged(variant) && requestedQuantity > availableQuantity) {
            this.showInventoryErrorModal(availableQuantity, requestedQuantity, variant.title);
            return false;
        }

        return true;
    }

    // Validate quantity input in real-time - IMPROVED to prevent excessive API calls
    async validateQuantityInput(input) {
        const form = input.closest('form');
        if (!form) return;

        const variantInput = form.querySelector('input[name="id"]:checked') ||
            form.querySelector('input[name="id"][type="hidden"]');

        if (!variantInput) return;

        const variantId = variantInput.value;
        const requestedQuantity = parseInt(input.value) || 1;
        const variant = this.getVariantInventory(variantId);

        if (!variant || !this.isInventoryManaged(variant)) return;

        const availableQuantity = await this.getAvailableQuantity(variantId);

        if (requestedQuantity > availableQuantity) {
            input.value = availableQuantity;
            if (availableQuantity === 0) {
                this.showInventoryErrorModal(0, requestedQuantity, variant.title);
            } else {
                this.showInventoryErrorModal(availableQuantity, requestedQuantity, variant.title);
            }
        }
    }

    // Setup variant change listener
    setupVariantChangeListener() {
        // Listen for radio button changes (size selection) - IMMEDIATE UPDATE
        document.addEventListener('change', (e) => {
            if (e.target && e.target.matches('input[name="id"][type="radio"]')) {
                this.clearCartCache(); // Clear cache when variant changes
                this.updateInventoryWarnings(); // Immediate update for responsive UI

                // CRITICAL: Force button state update for radio changes
                console.log('📻 Radio button changed - updating button state');
                setTimeout(() => {
                    // Double-check button state after radio change
                    const selectedRadio = document.querySelector('.size-option input[type="radio"]:checked:not(:disabled)');
                    const hasAvailable = document.querySelectorAll('.size-option input[type="radio"]:not(:disabled)').length > 0;
                    this.updateAddToCartButton(hasAvailable, selectedRadio);
                    this.refreshDropdownState();
                    console.log('🔄 Button state and dropdown double-checked after radio change');
                }, 50);
            }
        });

        // Listen for dropdown changes - IMMEDIATE UPDATE
        document.addEventListener('change', (e) => {
            if (e.target && e.target.matches('select[name="id"]')) {
                this.clearCartCache(); // Clear cache when variant changes
                this.updateInventoryWarnings(); // Immediate update for responsive UI
            }
        });
    }

    // Setup cart update listener to refresh inventory when items are added/removed
    setupCartUpdateListener() {
        // Listen for cart updates via fetch requests
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            const response = await originalFetch(...args);

            // Check if this was a cart-related request
            const url = args[0];
            if (typeof url === 'string' && (
                url.includes('/cart/add') ||
                url.includes('/cart/update') ||
                url.includes('/cart/change') ||
                url.includes('/cart/clear')
            )) {
                // Clear cache immediately and refresh inventory
                this.clearCartCache();
                setTimeout(() => this.updateInventoryWarnings(), 200);
            }

            return response;
        };

        // Also listen for custom cart events - IMMEDIATE UPDATES
        document.addEventListener('cart:updated', () => {
            this.clearCartCache();
            this.updateInventoryWarnings(); // Immediate update for responsive UI
        });

        // Listen for successful add to cart - IMMEDIATE UPDATE
        document.addEventListener('cart:item-added', () => {
            this.clearCartCache();
            this.updateInventoryWarnings(); // Immediate update for responsive UI
        });

        // Listen for cart item removal (CRITICAL FOR REAL-TIME UPDATES)
        document.addEventListener('cart:item-removed', () => {
            this.clearCartCache();
            this.updateInventoryWarnings(); // Immediate update for responsive UI
        });

        // Listen for variant changes - IMMEDIATE UPDATE for responsive UI
        document.addEventListener('variant:changed', (e) => {
            this.clearCartCache();
            // Run immediately to prevent "sold out" flashing
            this.updateInventoryWarnings();

            // ADDITIONAL: Set up a backup check in case the immediate update misses something
            setTimeout(() => {
                this.updateInventoryWarnings();
                console.log('🔄 Backup inventory check after variant change');
            }, 100);
        });

        // Listen for cart drawer closing (CRITICAL FOR REAL-TIME UPDATES)
        document.addEventListener('cart:drawer-closed', () => {
            this.clearCartCache();
            this.updateInventoryWarnings(); // Immediate update for responsive UI
        });
    }

    // Show inventory error modal - IMPROVED with better messaging
    showInventoryErrorModal(available, requested, variantTitle) {
        let message;

        // FIX: If variant title is "Default Title", get the actual product title
        let displayTitle = variantTitle;
        if (variantTitle === 'Default Title') {
            // Try to get the actual product title from the page
            const productTitleElement = document.querySelector('.product-title, h1');
            if (productTitleElement) {
                displayTitle = productTitleElement.textContent.trim();
            } else {
                displayTitle = 'this item'; // Fallback
            }
        }

        // Handle different error scenarios with more accurate messaging
        if (variantTitle === 'Please select a size before adding to cart' || variantTitle === 'Invalid product variant') {
            message = variantTitle;
        } else if (available === 0) {
            message = `Sorry, "${displayTitle}" is currently sold out.`;
        } else if (requested > available) {
            // FIXED: Show actual requested quantity, not hardcoded "3"
            message = `Sorry, only ${available} unit${available === 1 ? '' : 's'} of "${displayTitle}" ${available === 1 ? 'is' : 'are'} available. You tried to add ${requested}.`;
        } else {
            message = `Unable to add "${displayTitle}" to cart. Please try again.`;
        }

        const modalHTML = `
      <div id="inventory-error-modal" class="inventory-error-modal">
        <div class="inventory-error-modal__overlay"></div>
        <div class="inventory-error-modal__content">
          <div class="inventory-error-modal__message">${message}</div>
          <button class="inventory-error-modal__close" aria-label="Close">×</button>
        </div>
      </div>
    `;

        // Remove existing modal
        const existingModal = document.getElementById('inventory-error-modal');
        if (existingModal) {
            existingModal.remove();
        }

        // Add modal to page
        document.body.insertAdjacentHTML('beforeend', modalHTML);

        // Get modal elements
        const modal = document.getElementById('inventory-error-modal');
        const overlay = modal.querySelector('.inventory-error-modal__overlay');
        const closeBtn = modal.querySelector('.inventory-error-modal__close');

        // Show modal
        modal.classList.add('active');

        // IMMEDIATE: Check if user has already selected a different available size
        // This handles the case where they switched sizes before modal appeared
        setTimeout(() => {
            if (window.InventoryValidator) {
                window.InventoryValidator.updateInventoryWarnings();
                console.log('🔄 Checked button state while inventory error modal is showing');
            }
        }, 100);

        // Add event listeners to close modal
        const closeModal = () => {
            modal.classList.remove('active');
            setTimeout(() => {
                if (modal.parentNode) {
                    modal.parentNode.removeChild(modal);
                }

                // CRITICAL: Refresh button state after modal closes
                // The user might have selected a different available size while modal was open
                if (window.InventoryValidator) {
                    window.InventoryValidator.updateInventoryWarnings();
                    window.InventoryValidator.refreshDropdownState();
                    console.log('🔄 Refreshed button state and dropdown after inventory error modal closed');
                }
            }, 300);
        };

        overlay.addEventListener('click', closeModal);
        closeBtn.addEventListener('click', closeModal);

        // Close on escape key
        const handleEscape = function (e) {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);

        // Auto-close after 4 seconds (reduced from 5)
        setTimeout(closeModal, 4000);
    }
}

// Initialize when DOM is loaded - IMPROVED initialization
document.addEventListener('DOMContentLoaded', function () {
    // Only initialize on product pages or pages with product forms
    const hasProductForm = document.querySelector('form[action*="/cart/add"]');
    const isProductPage = document.body.classList.contains('template-product');

    if (hasProductForm || isProductPage) {
        // Verify inventory data exists and is valid
        console.log('🔍 Inventory data verification:', {
            productVariantsInventory_exists: !!window.productVariantsInventory,
            productVariantsInventory_count: window.productVariantsInventory ? Object.keys(window.productVariantsInventory).length : 0,
            cartInventoryData_exists: !!window.cartInventoryData,
            cartInventoryData_count: window.cartInventoryData ? Object.keys(window.cartInventoryData).length : 0
        });

        // Log first few variants for debugging
        if (window.productVariantsInventory) {
            const firstVariants = Object.entries(window.productVariantsInventory).slice(0, 3);
            console.log('🔍 Sample inventory data:', firstVariants);
        }

        window.InventoryValidator = new InventoryValidator();
        console.log('✅ Enhanced Inventory Validator initialized');

        // TESTING MODE: Add console helpers for Big Daddy
        if (window.location.hostname.includes('myshopify.com') || window.location.hostname === 'localhost') {
            window.testInventory = {
                // Simulate reducing inventory for testing
                simulateInventoryReduction: function (variantId, newQuantity) {
                    if (window.productVariantsInventory && window.productVariantsInventory[variantId]) {
                        const oldQuantity = window.productVariantsInventory[variantId].inventory_quantity;
                        window.productVariantsInventory[variantId].inventory_quantity = newQuantity;
                        console.log(`🧪 TEST: Reduced inventory for variant ${variantId} from ${oldQuantity} to ${newQuantity}`);

                        // Trigger inventory update
                        if (window.InventoryValidator) {
                            window.InventoryValidator.clearCartCache();
                            window.InventoryValidator.updateInventoryWarnings();
                        }
                    } else {
                        console.warn('❌ Variant not found:', variantId);
                    }
                },

                // Simulate restoring inventory
                restoreInventory: function (variantId, quantity) {
                    if (window.productVariantsInventory && window.productVariantsInventory[variantId]) {
                        window.productVariantsInventory[variantId].inventory_quantity = quantity;
                        console.log(`🔄 TEST: Restored inventory for variant ${variantId} to ${quantity}`);

                        // Trigger inventory update
                        if (window.InventoryValidator) {
                            window.InventoryValidator.clearCartCache();
                            window.InventoryValidator.updateInventoryWarnings();
                        }
                    }
                },

                // Show current inventory for all variants
                showAllInventory: function () {
                    console.log('📦 CURRENT INVENTORY STATUS:');
                    if (window.productVariantsInventory) {
                        Object.values(window.productVariantsInventory).forEach(variant => {
                            console.log(`- ${variant.title}: ${variant.inventory_quantity} units (Policy: ${variant.inventory_policy})`);
                        });
                    }
                },

                // Simulate sold out state
                setSoldOut: function (variantId) {
                    this.simulateInventoryReduction(variantId, 0);
                },

                // Help text
                help: function () {
                    console.log(`
🧪 INVENTORY TESTING COMMANDS:

testInventory.showAllInventory()          - Show current stock levels
testInventory.setSoldOut(variantId)       - Mark variant as sold out  
testInventory.simulateInventoryReduction(variantId, newQuantity) - Set specific stock level
testInventory.restoreInventory(variantId, quantity) - Restore stock to specific amount

EXAMPLE USAGE:
testInventory.showAllInventory()          // See all current stock
testInventory.setSoldOut('123456789')     // Make variant sold out
testInventory.restoreInventory('123456789', 5) // Give it 5 units back

This simulates stock changes WITHOUT actual purchases!
                    `);
                }
            };

            console.log(`
🧪 TESTING MODE ENABLED!
Type 'testInventory.help()' in console to see testing commands.
You can now simulate inventory changes without making purchases!
            `);
        }
    } else {
        console.log('⚠️ Skipping inventory validator - no product forms found');
    }
}); 